import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import airbnbApi from './api';
import { CodeSelect, ErrorAlert, MultiCodeSelect, PageLoader, useMasterData } from './common';

const CONTACT_ROLES = ['main', 'technical', 'reservations', 'customer_service', 'management', 'administration', 'finance'];
const AIRBNB_CHANNEL = 'AIR298';

const emptyContact = role => ({
  role, title: '', firstName: '', middleName: '', lastName: '', email: '', telephone: '', mobilePhone: '',
  address: '', city: '', postalCode: '', country: '', state: '', region: ''
});

const emptyForm = {
  company_name: '', company_email: '', company_phone: '', website: '',
  address: '', city: '', postal_code: '', country_code: '', main_currency: '',
  accepted_currencies: [], accepted_payment_methods: [], spoken_languages: ['en'],
  check_in_from: '15:00', check_in_until: '22:00', check_out_from: '07:00', check_out_until: '11:00',
  contacts: [emptyContact('main')],
  invoice_details: {
    entityName: '', email: '', firstName: '', lastName: '', telephone: '', mobile: '',
    entityAddress1: '', entityAddress2: '', entityCity: '', entityPostalCode: '',
    entityCountry: '', entityState: '', entityRegion: '',
    registrationNumber: '', taxNumber: '', bicSwiftCode: '', ibanAccountNumber: ''
  },
  airbnb_host_id: '', airbnb_channel_policy_id: '', airbnb_policy_countries: [], airbnb_settings: {}
};

const Field = ({ label, required, children, md = 4 }) => (
  <Col md={md} className="margbot10">
    <Form.Label className="fnt13 mb-1">{label}{required && <span className="text-danger"> *</span>}</Form.Label>
    {children}
  </Col>
);

const TextField = ({ label, value, onChange, required, type = 'text', md, disabled }) => (
  <Field label={label} required={required} md={md}>
    <Form.Control type={type} value={value || ''} required={required} disabled={disabled}
      onChange={e => onChange(e.target.value)} />
  </Field>
);

// Country → state → region selects; clearing a parent clears its children
const LocationFields = ({ country, state, region, onChange, required }) => (
  <>
    <Field label="Country" required={required}>
      <CodeSelect category="countries" value={country} required={required}
        onChange={v => onChange({ country: v, state: '', region: '' })} />
    </Field>
    <Field label="State" required={required}>
      <CodeSelect category="states" parentCode={country || ''} value={state} required={required}
        disabled={!country} onChange={v => onChange({ state: v, region: '' })} />
    </Field>
    <Field label="Region" required={required}>
      <CodeSelect category="regions" parentCode={state || ''} value={region} required={required}
        disabled={!state} onChange={v => onChange({ region: v })} />
    </Field>
  </>
);

const settingOptions = setting => ((setting.data && setting.data.settingOptions) || []).map(o => (
  typeof o === 'object' && o !== null
    ? { value: String(o.value ?? o.code ?? o.id), label: o.name || o.label || String(o.value ?? o.code ?? o.id) }
    : { value: String(o), label: String(o) }
));

const GROUP_LABELS = {
  regulatory_settings: 'Regulatory',
  credentials: 'Host account',
  booking_settings: 'Booking',
  content_settings: 'Content'
};
const groupLabel = group => GROUP_LABELS[group] || (group || 'other').replace(/_/g, ' ');

// One input per NextPax settingType (bool, checkbox, single_select, integer, textarea, string)
const SettingInput = ({ setting, value, onChange }) => {
  const type = (setting.data && setting.data.settingType) || 'string';
  const options = settingOptions(setting);
  const current = value === undefined || value === null ? '' : String(value);

  if (type === 'bool' || type === 'checkbox') {
    return (
      <Form.Select value={current} onChange={e => onChange(e.target.value)}>
        <option value="">Not set</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </Form.Select>
    );
  }
  if (options.length > 0) {
    const multi = /multi/i.test(type);
    if (multi) {
      return (
        <Form.Select multiple htmlSize={Math.min(options.length, 5)} value={current.split(',').filter(Boolean)}
          onChange={e => onChange(Array.from(e.target.selectedOptions).map(o => o.value).join(','))}>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Form.Select>
      );
    }
    return (
      <Form.Select value={current} onChange={e => onChange(e.target.value)}>
        <option value="">Not set</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </Form.Select>
    );
  }
  if (type === 'textarea') {
    return <Form.Control as="textarea" rows={2} value={current} onChange={e => onChange(e.target.value)} />;
  }
  return (
    <Form.Control type={type === 'integer' ? 'number' : 'text'} value={current} onChange={e => onChange(e.target.value)} />
  );
};

// Airbnb PM settings, built from master data (channel_settings under AIR298):
// labels, input types, options and "required" all come from NextPax.
const AirbnbSettings = ({ value, onChange }) => {
  const settings = useMasterData('channel_settings', AIRBNB_CHANNEL);
  const [showOptional, setShowOptional] = useState(false);
  const set = (code, v) => onChange({ ...value, [code]: v });

  if (settings.length === 0) {
    return (
      <div className="text-muted fnt13">
        Airbnb settings are not in master data yet. Sync master data first (Master data → Sync now).
      </div>
    );
  }

  const isRequired = s => !!(s.data && s.data.settingRequired);
  const required = settings.filter(isRequired);
  const optional = settings.filter(s => !isRequired(s));
  const groups = optional.reduce((acc, s) => {
    const group = (s.data && s.data.settingGroup) || 'other';
    (acc[group] = acc[group] || []).push(s);
    return acc;
  }, {});
  const optionalSet = optional.filter(s => value[s.code] !== undefined && value[s.code] !== '').length;

  const renderSetting = (setting, md = 6) => (
    <Field key={setting.code} label={setting.name || setting.code} required={isRequired(setting)} md={md}>
      <SettingInput setting={setting} value={value[setting.code]} onChange={v => set(setting.code, v)} />
      <Form.Text className="fnt11">{setting.code}</Form.Text>
    </Field>
  );

  return (
    <>
      <div className="fnt13 bld margbot5">Required by Airbnb</div>
      <Row>{required.map(s => renderSetting(s))}</Row>
      <Button size="sm" variant="link" className="ps-0" onClick={() => setShowOptional(v => !v)}>
        {showOptional ? 'Hide' : 'Show'} optional settings ({optional.length}{optionalSet ? `, ${optionalSet} set` : ''})
      </Button>
      {showOptional && Object.entries(groups).map(([group, list]) => (
        <div key={group} className="margtop10">
          <div className="fnt13 bld text-capitalize margbot5">{groupLabel(group)}</div>
          <Row>{list.map(s => renderSetting(s, 4))}</Row>
        </div>
      ))}
    </>
  );
};

// Drop blank optional strings so Rails stores NULL instead of ""
const compact = obj => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== '' && v !== null && v !== undefined));

const PropertyManagerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(id ? null : emptyForm);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    airbnbApi.getPropertyManager(id)
      .then(pm => {
        const merged = { ...emptyForm };
        Object.keys(emptyForm).forEach(key => {
          if (pm[key] !== null && pm[key] !== undefined) merged[key] = pm[key];
        });
        merged.invoice_details = { ...emptyForm.invoice_details, ...(pm.invoice_details || {}) };
        merged.contacts = (pm.contacts && pm.contacts.length ? pm.contacts : [emptyContact('main')])
          .map(c => ({ ...emptyContact(c.role), ...c }));
        merged.airbnb_settings = pm.airbnb_settings || {};
        setForm(merged);
      })
      .catch(setError);
  }, [id]);

  if (!form) return <div className="pad20"><ErrorAlert error={error} />{!error && <PageLoader />}</div>;

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const setInvoice = changes => setForm(prev => ({ ...prev, invoice_details: { ...prev.invoice_details, ...changes } }));
  const setContact = (index, changes) => setForm(prev => ({
    ...prev,
    contacts: prev.contacts.map((c, i) => (i === index ? { ...c, ...changes } : c))
  }));
  const addContact = () => {
    const used = form.contacts.map(c => c.role);
    set('contacts', [...form.contacts, emptyContact(CONTACT_ROLES.find(r => !used.includes(r)) || 'technical')]);
  };
  const removeContact = index => set('contacts', form.contacts.filter((_, i) => i !== index));

  const submit = e => {
    e.preventDefault();
    if (form.contacts.filter(c => c.role === 'main').length !== 1) {
      setError({ message: 'Exactly one contact must have the role "main".' });
      return;
    }
    const payload = {
      ...form,
      company_phone: form.company_phone || null,
      website: form.website || null,
      airbnb_host_id: form.airbnb_host_id || null,
      airbnb_channel_policy_id: form.airbnb_channel_policy_id || null,
      contacts: form.contacts.map(compact),
      invoice_details: compact(form.invoice_details),
      airbnb_settings: compact(form.airbnb_settings)
    };
    setSaving(true);
    setError(null);
    const save = id ? airbnbApi.updatePropertyManager(id, payload) : airbnbApi.createPropertyManager(payload);
    save
      .then(pm => navigate(`/airbnb/property-managers/${pm.id}`))
      .catch(err => { setError(err); window.scrollTo(0, 0); })
      .finally(() => setSaving(false));
  };

  const inv = form.invoice_details;

  return (
    <div className="container-fluid pad20 text-start">
      <div className="margbot10 fnt13">
        <Link to="/airbnb">Property managers</Link>{id && <> / <Link to={`/airbnb/property-managers/${id}`}>{form.company_name}</Link></>}
      </div>
      <h4>{id ? 'Edit property manager' : 'New property manager'}</h4>
      <ErrorAlert error={error} onClose={() => setError(null)} />

      <Form onSubmit={submit}>
        <Card className="margbot15">
          <Card.Header className="bld">Company</Card.Header>
          <Card.Body>
            <Row>
              <TextField label="Company name" value={form.company_name} onChange={v => set('company_name', v)} required />
              <TextField label="Company email" type="email" value={form.company_email} onChange={v => set('company_email', v)} required />
              <TextField label="Company phone" value={form.company_phone} onChange={v => set('company_phone', v)} />
              <TextField label="Website" value={form.website} onChange={v => set('website', v)} />
              <TextField label="Address" value={form.address} onChange={v => set('address', v)} required md={8} />
              <TextField label="City" value={form.city} onChange={v => set('city', v)} required />
              <TextField label="Postal code" value={form.postal_code} onChange={v => set('postal_code', v)} required />
              <Field label="Country" required>
                <CodeSelect category="countries" value={form.country_code} onChange={v => set('country_code', v)} required />
              </Field>
              <Field label="Main currency" required>
                <CodeSelect category="currencies" value={form.main_currency} onChange={v => set('main_currency', v)} required />
              </Field>
              <Field label="Accepted currencies" md={4}>
                <MultiCodeSelect category="currencies" value={form.accepted_currencies} onChange={v => set('accepted_currencies', v)} />
                <Form.Text className="fnt11">Defaults to the main currency</Form.Text>
              </Field>
              <Field label="Accepted payment methods" required md={4}>
                <MultiCodeSelect category="payment_methods" value={form.accepted_payment_methods} onChange={v => set('accepted_payment_methods', v)} />
              </Field>
              <Field label="Spoken languages" required md={4}>
                <MultiCodeSelect category="languages" value={form.spoken_languages} onChange={v => set('spoken_languages', v)} />
              </Field>
              <TextField label="Check-in from" type="time" md={3} value={form.check_in_from} onChange={v => set('check_in_from', v)} required />
              <TextField label="Check-in until" type="time" md={3} value={form.check_in_until} onChange={v => set('check_in_until', v)} required />
              <TextField label="Check-out from" type="time" md={3} value={form.check_out_from} onChange={v => set('check_out_from', v)} required />
              <TextField label="Check-out until" type="time" md={3} value={form.check_out_until} onChange={v => set('check_out_until', v)} required />
            </Row>
          </Card.Body>
        </Card>

        <Card className="margbot15">
          <Card.Header className="bld d-flex justify-content-between align-items-center">
            Contacts
            <Button size="sm" variant="outline-primary" onClick={addContact}>Add contact</Button>
          </Card.Header>
          <Card.Body>
            {form.contacts.map((c, index) => (
              <div key={index} className={index > 0 ? 'border-top pt-3 mt-2' : ''}>
                <Row>
                  <Field label="Role" required md={3}>
                    <Form.Select value={c.role} onChange={e => setContact(index, { role: e.target.value })}>
                      {CONTACT_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </Form.Select>
                  </Field>
                  <TextField label="Title" md={1} value={c.title} onChange={v => setContact(index, { title: v })} />
                  <TextField label="First name" md={3} value={c.firstName} onChange={v => setContact(index, { firstName: v })} required />
                  <TextField label="Middle name" md={2} value={c.middleName} onChange={v => setContact(index, { middleName: v })} />
                  <TextField label="Last name" md={3} value={c.lastName} onChange={v => setContact(index, { lastName: v })} required />
                  <TextField label="Email" type="email" value={c.email} onChange={v => setContact(index, { email: v })} required />
                  <TextField label="Telephone" value={c.telephone} onChange={v => setContact(index, { telephone: v })} required />
                  <TextField label="Mobile" value={c.mobilePhone} onChange={v => setContact(index, { mobilePhone: v })} />
                  <TextField label="Address" md={6} value={c.address} onChange={v => setContact(index, { address: v })} required />
                  <TextField label="City" md={3} value={c.city} onChange={v => setContact(index, { city: v })} required />
                  <TextField label="Postal code" md={3} value={c.postalCode} onChange={v => setContact(index, { postalCode: v })} required />
                  <LocationFields country={c.country} state={c.state} region={c.region} required
                    onChange={changes => setContact(index, changes)} />
                </Row>
                {form.contacts.length > 1 && (
                  <Button size="sm" variant="outline-danger" onClick={() => removeContact(index)}>Remove contact</Button>
                )}
              </div>
            ))}
          </Card.Body>
        </Card>

        <Card className="margbot15">
          <Card.Header className="bld">Invoice details</Card.Header>
          <Card.Body>
            <Row>
              <TextField label="Entity name" value={inv.entityName} onChange={v => setInvoice({ entityName: v })} required />
              <TextField label="Email" type="email" value={inv.email} onChange={v => setInvoice({ email: v })} required />
              <TextField label="Telephone" value={inv.telephone} onChange={v => setInvoice({ telephone: v })} required />
              <TextField label="First name" value={inv.firstName} onChange={v => setInvoice({ firstName: v })} required />
              <TextField label="Last name" value={inv.lastName} onChange={v => setInvoice({ lastName: v })} required />
              <TextField label="Mobile" value={inv.mobile} onChange={v => setInvoice({ mobile: v })} />
              <TextField label="Address line 1" md={6} value={inv.entityAddress1} onChange={v => setInvoice({ entityAddress1: v })} required />
              <TextField label="Address line 2" md={6} value={inv.entityAddress2} onChange={v => setInvoice({ entityAddress2: v })} />
              <TextField label="City" value={inv.entityCity} onChange={v => setInvoice({ entityCity: v })} required />
              <TextField label="Postal code" value={inv.entityPostalCode} onChange={v => setInvoice({ entityPostalCode: v })} required />
              <Col md={4} />
              <LocationFields country={inv.entityCountry} state={inv.entityState} region={inv.entityRegion} required
                onChange={changes => setInvoice(Object.fromEntries(Object.entries(changes).map(([key, v]) => [
                  { country: 'entityCountry', state: 'entityState', region: 'entityRegion' }[key], v
                ])))} />
              <TextField label="Registration number" value={inv.registrationNumber} onChange={v => setInvoice({ registrationNumber: v })} />
              <TextField label="Tax number" value={inv.taxNumber} onChange={v => setInvoice({ taxNumber: v })} />
              <Col md={4} />
              <TextField label="BIC / SWIFT" value={inv.bicSwiftCode} onChange={v => setInvoice({ bicSwiftCode: v })} />
              <TextField label="IBAN / account number" value={inv.ibanAccountNumber} onChange={v => setInvoice({ ibanAccountNumber: v })} />
            </Row>
          </Card.Body>
        </Card>

        <Card className="margbot15">
          <Card.Header className="bld">Airbnb</Card.Header>
          <Card.Body>
            <Row>
              <TextField label="Airbnb host ID" value={form.airbnb_host_id} onChange={v => set('airbnb_host_id', v)} md={6} />
              <Field label="Airbnb cancellation policy" md={6}>
                <CodeSelect category="channel_policies" parentCode={AIRBNB_CHANNEL} value={form.airbnb_channel_policy_id}
                  onChange={v => set('airbnb_channel_policy_id', v)} />
              </Field>
              <Field label="Countries covered by the Airbnb policy" md={6}>
                <MultiCodeSelect category="countries" value={form.airbnb_policy_countries}
                  onChange={v => set('airbnb_policy_countries', v)} />
                <Form.Text className="fnt11">
                  Must include the country of every property, or "Send cancellation policies" stays blocked.
                </Form.Text>
              </Field>
            </Row>
            <Form.Text className="fnt12 d-block margbot10">
              The host ID is the default for all properties and can't be changed once a property is enabled on Airbnb.
            </Form.Text>
            <div className="bld margbot5">Settings</div>
            <AirbnbSettings value={form.airbnb_settings} onChange={v => set('airbnb_settings', v)} />
          </Card.Body>
        </Card>

        <div className="margbot20">
          <Button type="submit" disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" /> : (id ? 'Save changes' : 'Create property manager')}
          </Button>
          <Link to={id ? `/airbnb/property-managers/${id}` : '/airbnb'} className="btn btn-link">Cancel</Link>
        </div>
      </Form>
    </div>
  );
};

export default PropertyManagerForm;
